import React, {ElementType, ReactElement} from 'react'
import {MdKeyboardArrowLeft as ChevronLeft} from 'react-icons/md'

import PageWidthContainer from '../../../../designSystem/helpers/PageWidthContainer'
import {isFindHotelBrand} from '@daedalus/core/src/customerSupport/business'
import WithAuthEnabled from '@daedalus/core/src/auth/components/WithAuthEnabled'
import urls from '@daedalus/core/src/utils/url'
import {defaultBrand} from '@daedalus/core/src/brand/config'
import BrandLogo from '../../../../designSystem/BrandLogo'
import Grid from '../../../../designSystem/helpers/Grid'
import {useDeviceLayout} from '../../../../../context/deviceLayout'

import {
  HeaderElement,
  HeaderGrid,
  CenterGrid,
  SideGrid,
  FullWidthCenterGrid,
  BackButtonElement,
  LogoElement,
  LogoLinkElement,
  LogoAnchorElement,
  HeaderControlsWrapper,
  UserControlWrapper,
  PreferencesWidgetWrapper
} from './styles'

import {BrandType} from '@daedalus/core/src/brand/types'
import {RangeSizesType} from 'types/Sizes'
import {toggle} from 'opticks/lib/optimizely'

interface PropsType {
  /** A user widget representing a logged in user */
  UserWidget: ElementType
  /** The component containing the preferences widget and popup form */
  PreferencesWidget: ElementType
  /** Brand configuration. It can be FindHotel or Etrip */
  brand?: BrandType
  /** Element to display in the middle of the header. Mainly used for the search form. */
  children?: ReactElement
  /** Whether to disable the click action in the logo */
  disableLogoClick?: boolean
  /** Whether to display the preferences bar */
  disablePreferences?: boolean
  /** Should it render the currency selector */
  hasCurrencySelector?: boolean
  /** An action fired when the user clicks on the side action button */
  onActionButtonClick?: () => void
  /** Whether to display the back button  */
  showBackButton?: boolean
  /** Which url should the logo send the user when clicked upon */
  logoUrl?: string
  /** Whether the header should span full width */
  isFullWidth?: boolean
}

interface LogoType {
  disableLogoClick?: boolean
  brand: BrandType
  logoUrl?: string
}

interface BackButtonType {
  showBackButton?: boolean
  handleBackClick: PropsType['onActionButtonClick']
}

const Logo = ({disableLogoClick, brand, logoUrl}: LogoType) => {
  if (disableLogoClick) {
    return (
      <LogoElement brand={brand.code}>
        <BrandLogo brand={brand} />
      </LogoElement>
    )
  }

  if (logoUrl) {
    return (
      <LogoAnchorElement brand={brand.code} href={logoUrl}>
        <BrandLogo brand={brand} />
      </LogoAnchorElement>
    )
  }

  return (
    <LogoLinkElement brand={brand.code} to={urls.root}>
      <BrandLogo brand={brand} />
    </LogoLinkElement>
  )
}

const BackButton = ({showBackButton, handleBackClick}: BackButtonType) =>
  showBackButton ? (
    <BackButtonElement onClick={handleBackClick}>
      <ChevronLeft size={24} />
    </BackButtonElement>
  ) : null

const Header = ({
  brand = defaultBrand,
  children,
  disablePreferences,
  hasCurrencySelector,
  UserWidget,
  PreferencesWidget,
  showBackButton,
  disableLogoClick,
  logoUrl,
  onActionButtonClick,
  isFullWidth = false
}: PropsType) => {
  const {isDesktopSm, isDesktopXs} = useDeviceLayout()

  const searchBoxShouldMoveBelowHeaderGrid = toggle(
    'yw28dhq1-top-bar-improvements',
    isDesktopXs,
    Boolean(isDesktopXs || isDesktopSm)
  )

  const handleBackClick = (): void => {
    onActionButtonClick?.()
  }

  return (
    <HeaderElement>
      <PageWidthContainer
        paddingDesktopXsSize={
          toggle('dq82hd-desktop-map', 's500', 's600') as RangeSizesType
        }
        isFullWidth={isFullWidth}
      >
        <HeaderGrid
          container
          justify="space-between"
          spacing="s000"
          alignItems="center"
          wrap="nowrap"
        >
          <SideGrid container spacing="s000">
            {showBackButton && (
              <Grid>
                <BackButton
                  showBackButton={showBackButton}
                  handleBackClick={handleBackClick}
                />
              </Grid>
            )}
            <Grid>
              <Logo
                disableLogoClick={disableLogoClick}
                brand={brand}
                logoUrl={logoUrl}
              />
            </Grid>
          </SideGrid>
          {!searchBoxShouldMoveBelowHeaderGrid && (
            <CenterGrid container spacing="s000" justify="center">
              {children}
            </CenterGrid>
          )}
          {!disablePreferences && (
            <SideGrid container spacing="s000" justify="flex-end">
              <HeaderControlsWrapper>
                <PreferencesWidgetWrapper>
                  <PreferencesWidget hasCurrency={hasCurrencySelector} />
                </PreferencesWidgetWrapper>
                <WithAuthEnabled>
                  {isFindHotelBrand(brand.code) && (
                    <UserControlWrapper>
                      {UserWidget && <UserWidget />}
                    </UserControlWrapper>
                  )}
                </WithAuthEnabled>
              </HeaderControlsWrapper>
            </SideGrid>
          )}
        </HeaderGrid>
        {searchBoxShouldMoveBelowHeaderGrid && Boolean(children) && (
          <FullWidthCenterGrid
            container
            justify="center"
            spacing="s000"
            alignItems="center"
            wrap="nowrap"
          >
            {children}
          </FullWidthCenterGrid>
        )}
      </PageWidthContainer>
    </HeaderElement>
  )
}

export default Header
